import React, { useEffect, useState } from "react";
import { PlSheet } from "../list/ProductListBits";
import { clearPhotoUpdate, requestPhotoUpdate, saveDyeStatus } from "./photoUpdateApi";
import type { DyeStatus, PhotoReason, PhotoStatus } from "./photoUpdateApi";
import NeedsPhotoBadge from "./NeedsPhotoBadge";
import { DYE_LABELS, MANUAL_REASONS, REASON_LABELS, buyerColourLine, openFor } from "./photoStatus";
import ShadeLinkSheet from "../shade/ShadeLinkSheet";
import { SHADE_STRINGS } from "../shade/shadeStrings";

type Props = {
  productId: string;
  colour?: string | null;
  status?: PhotoStatus | null;
  role?: string;
  /** Opens the editor's photo step ("Add photo"). */
  onAddPhoto: () => void;
  /** After a dye change the buyer colour moved: reload the piece. */
  onChanged: () => void;
  /** A worker upload link was made (the Shade photos panel lists open links). */
  onLinkMade?: () => void;
};

const DYES: DyeStatus[] = ["undyed", "in_dyeing", "dyed"];

/**
 * Photos & dye card on /product/:id. One primary action: Add photo while a photo is needed. Admins can ask
 * for photos by hand or clear a stored request (no_photo clears itself). Dyeable pieces show Undyed · In dyeing ·
 * Dyed; Dyed asks for the dyed colour and shows the buyer colour before anything is saved.
 * Renders nothing when the list row carried no photoStatus (feature off).
 */
const PhotoUpdatePanel = ({ productId, colour, status, role, onAddPhoto, onChanged, onLinkMade }: Props) => {
  const [view, setView] = useState<PhotoStatus | null | undefined>(status);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState<PhotoReason>("poor_quality");
  const [note, setNote] = useState("");
  const [dyeOpen, setDyeOpen] = useState(false);
  const [dyedColour, setDyedColour] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);
  useEffect(() => setView(status), [status]);
  if (!view) return null;

  const isAdmin = role === "ORGANIZATION_ADMIN";
  const isStaff = isAdmin || role === "ORGANIZATION_MANAGER";
  const current: DyeStatus = (view.dyeStatus as DyeStatus) || "undyed";

  const run = async (call: () => Promise<any>, after?: () => void) => {
    setBusy(true);
    setError("");
    try {
      const res = await call();
      if (res?.success && res?.data) {
        setView(res.data);
        after?.();
        return true;
      }
      setError(res?.message || "Could not save. Try again.");
    } catch {
      setError("No connection. Try again.");
    } finally {
      setBusy(false);
    }
    return false;
  };

  const pickDye = (next: DyeStatus) => {
    if (next === current || busy) return;
    if (next === "dyed") {
      setDyedColour(view.dyedColour || "");
      setDyeOpen(true);
      return;
    }
    void run(() => saveDyeStatus(productId, { dyeStatus: next, expectedDyeStatus: current }));
  };

  const derived = view.source === "derived";
  const buyerLine = buyerColourLine(dyedColour, colour);

  return (
    <div className="vp-card pu-panel">
      <div className="vp-panel__head">
        <span className="vp-card__title">Photos{view.dyeable ? " & dye" : ""}</span>
        <NeedsPhotoBadge status={view} />
      </div>

      {view.needsPhoto ? (
        <div className="pu-need">
          <div className="pu-need__text">
            <b>{view.reason ? REASON_LABELS[view.reason] : "Needs photo"}</b>
            {openFor(view.daysOpen) ? <span className="pu-dim"> · {openFor(view.daysOpen)}</span> : null}
            {view.note ? <div className="pu-dim pu-note">{view.note}</div> : null}
            {derived ? <div className="pu-dim">Clears when a main photo is added.</div> : null}
          </div>
          <div className="pu-actions">
            {isAdmin && !derived && view.status === "uploaded" ? (
              <a className="pl-btn is-primary is-sm" href="#shade-photos" onClick={(event) => {
                event.preventDefault();
                document.getElementById("shade-photos")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}>
                {SHADE_STRINGS.reviewPhotos}
              </a>
            ) : null}
            {isAdmin && !derived ? (
              <button type="button" className={`pl-btn ${view.status === "uploaded" ? "is-ghost" : "is-primary"} is-sm`} onClick={() => setLinkOpen(true)}>
                {SHADE_STRINGS.getLink}
              </button>
            ) : null}
            <button type="button" className={`pl-btn ${isAdmin && !derived ? "is-ghost" : "is-primary"} is-sm`} onClick={onAddPhoto}>
              Add photo
            </button>
            {isAdmin && view.clearable ? (
              <button type="button" className="pl-btn is-ghost is-sm" disabled={busy} onClick={() => void run(() => clearPhotoUpdate(productId))}>
                Clear
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="pu-need">
          <span className="pu-dim">Photos are up to date.</span>
          {isAdmin && !asking ? (
            <button type="button" className="pl-btn is-ghost is-sm" onClick={() => setAsking(true)}>
              Ask for photo
            </button>
          ) : null}
        </div>
      )}

      {asking ? (
        <form
          className="pu-ask"
          onSubmit={(event) => {
            event.preventDefault();
            void run(() => requestPhotoUpdate(productId, { reason, note: note.trim() || undefined }), () => {
              setAsking(false);
              setNote("");
            });
          }}
        >
          <label className="pu-field">
            <span>Why</span>
            <select value={reason} onChange={(event) => setReason(event.target.value as PhotoReason)}>
              {MANUAL_REASONS.map((r) => (
                <option key={r} value={r}>
                  {REASON_LABELS[r]}
                </option>
              ))}
            </select>
          </label>
          <label className="pu-field">
            <span>Note (optional)</span>
            <input value={note} maxLength={500} onChange={(event) => setNote(event.target.value)} placeholder="e.g. border not visible" />
          </label>
          <div className="pu-actions">
            <button type="submit" className="pl-btn is-primary is-sm" disabled={busy}>
              Ask for photo
            </button>
            <button type="button" className="pl-btn is-ghost is-sm" onClick={() => setAsking(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {view.dyeable ? (
        <div className="pu-dye">
          <div className="pu-dye__label">
            Dye status
            {view.dyeableGuessed && !view.dyeStatus ? <span className="pu-dim"> · guessed from the name or colour</span> : null}
          </div>
          <div className="pu-seg" role="radiogroup" aria-label="Dye status">
            {DYES.map((d) => (
              <button
                key={d}
                type="button"
                role="radio"
                aria-checked={current === d}
                className={`pu-seg__opt${current === d ? " is-on" : ""}`}
                disabled={!isStaff || busy}
                onClick={() => pickDye(d)}
              >
                {DYE_LABELS[d]}
              </button>
            ))}
          </div>
          {current === "dyed" && view.dyedColour ? <div className="pu-dim">Dyed {view.dyedColour}</div> : null}
          {isStaff ? (
            <button type="button" className="pu-link" disabled={busy} onClick={() => void run(() => saveDyeStatus(productId, { dyeable: false }))}>
              Not a dyeable piece
            </button>
          ) : null}
        </div>
      ) : isStaff ? (
        <button type="button" className="pu-link" disabled={busy} onClick={() => void run(() => saveDyeStatus(productId, { dyeable: true }))}>
          This piece is dyed after weaving
        </button>
      ) : null}

      {error ? (
        <div className="pu-error" role="alert">
          {error}
        </div>
      ) : null}

      {isAdmin ? <ShadeLinkSheet open={linkOpen} onClose={() => setLinkOpen(false)} productId={productId} onMade={onLinkMade} /> : null}

      <PlSheet open={dyeOpen} onClose={() => setDyeOpen(false)} label="Mark as dyed" className="pu-sheet">
        {(close) => (
          <form
            className="pu-sheet__body"
            onSubmit={(event) => {
              event.preventDefault();
              if (!dyedColour.trim()) return;
              void run(
                () => saveDyeStatus(productId, { dyeStatus: "dyed", dyedColour: dyedColour.trim(), expectedDyeStatus: current }),
                () => {
                  close();
                  onChanged();
                }
              );
            }}
          >
            <h3 className="pu-sheet__title">Mark as dyed</h3>
            <label className="pu-field">
              <span>Dyed colour</span>
              <input value={dyedColour} maxLength={60} onChange={(event) => setDyedColour(event.target.value)} placeholder="e.g. Rani pink" autoFocus />
            </label>
            {buyerLine ? (
              <p className="pu-buyer" aria-live="polite">
                {buyerLine}
              </p>
            ) : null}
            <p className="pu-dim">The piece will be marked as needing new photos.</p>
            <div className="pu-actions">
              <button type="submit" className="pl-btn is-primary" disabled={busy || !dyedColour.trim()}>
                Save as dyed
              </button>
              <button type="button" className="pl-btn is-ghost" onClick={close}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </PlSheet>
    </div>
  );
};

export default PhotoUpdatePanel;
